/**
 * Check if an ID matches the P{number} format (e.g., P5, P123)
 */
export function isPNumberFormat(id: string): boolean {
  return /^P\d+$/.test(id);
}

/**
 * Check if an ID matches the A{Roman numeral} format (e.g., AIV, AXII)
 */
export function isARomanNumeralFormat(id: string): boolean {
  return /^A[IVXLCDM]+$/i.test(id);
}

/**
 * Convert Roman numeral string to a number.
 * Supports common Roman numerals: I, II, III, IV, V, VI, VII, VIII, IX, X, XI, XII, etc.
 * Returns null if the conversion fails (invalid Roman numeral).
 */
export function romanToNumber(roman: string): number | null {
  const upperRoman = roman.toUpperCase();

  // Roman numeral to number mapping
  const romanMap: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  };

  let result = 0;
  let i = 0;

  while (i < upperRoman.length) {
    const current = romanMap[upperRoman[i]];
    const next = romanMap[upperRoman[i + 1]];

    if (current === undefined) {
      // Invalid character found
      return null;
    }

    if (next !== undefined && current < next) {
      // Subtraction case (e.g., IV, IX, XL, XC, CD, CM)
      result += next - current;
      i += 2;
    } else {
      // Addition case
      result += current;
      i += 1;
    }
  }

  return result;
}

/**
 * Generate alternative IDs based on format rules:
 * - For P{number}: return [AECP{number}, ICP{number}]
 * - For A{Roman numeral}: return [A{number}] (converted numeral)
 * - For other formats: return empty array
 */
export function generateAlternativeIds(originalId: string): string[] {
  const alternatives: string[] = [];

  // Check for P{number} format
  if (isPNumberFormat(originalId)) {
    const number = originalId.substring(1); // Extract number part after 'P'
    alternatives.push(`AECP${number}`);
    alternatives.push(`ICP${number}`);
    return alternatives;
  }

  // Check for A{Roman numeral} format
  if (isARomanNumeralFormat(originalId)) {
    const romanNumeral = originalId.substring(1); // Extract Roman numeral part after 'A'
    const number = romanToNumber(romanNumeral);
    if (number !== null) {
      alternatives.push(`A${number}`);
    }
    return alternatives;
  }

  // No alternatives for other formats
  return alternatives;
}
